import api from './api';

export interface RawMaterialAllocation {
  item_number: string;
  transfer_number: string | null;
  allocated_weight: number;
  cases?: number;
  is_on_hold?: boolean;
}

// Station allocation for multi-station mode
export interface StationMaterialAllocation {
  item_number: string;
  transfer_number: string;
  allocated_weight: number;
}

export interface StationAllocation {
  station_id: number | null;  // null = unassigned sub-session
  materials: StationMaterialAllocation[];
  weight_mode?: 'fixed' | 'catch';  // Fixed = auto-use product unit_weight, Catch = manual entry
}

export interface CreateProductionSessionRequest {
  // New multi-material format
  raw_materials?: RawMaterialAllocation[];
  // Legacy single-material format (for backward compatibility)
  raw_material_lot?: string;
  product_sku: string;
  initial_material_weight?: number;
  target_output_weight: number;
  total_boxes?: number;
  notes?: string;
  station_id?: number;
  scheduled_date?: string;
  // Multi-station mode: array of station allocations
  station_allocations?: StationAllocation[];
  // Plan-only mode: create session without material allocation
  plan_only?: boolean;
  // Source warehouse code for auto-creating inbound transfer
  source_warehouse?: string;
  // Destination location for draft transfer back (outbound HTE → destination)
  transfer_back_destination?: string;
  // Override transfer date (YYYY-MM-DD); defaults to day before scheduled_date
  transfer_date?: string;
}

export interface SubSession {
  sub_session_number: string;
  session_number: string;
  station_id: number;
  station_name?: string;
  assigned_operator?: number;
  operator_name?: string;
  operator_employee_number?: string;
  assigned_packer?: string;
  packer_name?: string;
  packer_employee_number?: string;
  status: string;
  initial_material_weight: number;
  target_output_weight: number;  // Yield-adjusted expected output weight
  actual_output_weight: number;
  waste_weight: number;
  yield_percentage: number;
  started_at?: string;
  completed_at?: string;
  paused_duration_seconds?: number;
  weight_mode?: 'fixed' | 'catch';  // Fixed = auto-use product unit_weight, Catch = manual entry
  expected_yield_factor?: number;  // Yield factor at creation (e.g., 92.20 for 92.2%)
}

// ============================================
// DAILY WORK QUEUE TYPES
// ============================================

export interface DailyWorkQueueItem {
  sub_session_number: string;
  session_number: string;
  product_name: string;
  product_chinese_name: string | null;
  product_sku: string;
  station_id: number | null;
  station_name: string | null;
  operator_name: string | null;
  operator_employee_number: string | null;
  packer_name: string | null;
  packer_employee_number: string | null;
  status: string;
  expected_boxes: number;
  boxes_produced: number;
  scheduled_date: string | null;
  // Extended fields for production progress view
  actual_output_weight?: number;
  target_output_weight?: number;
  initial_material_weight?: number;
  yield_percentage?: number;
  started_at?: string | null;
  paused_duration_seconds?: number;
  queue_position?: number | null;
  operator_status?: 'active' | 'on_break' | 'scanned_out';
  packer_status?: 'active' | 'on_break' | 'scanned_out';
}

// ============================================
// END OF DAY SUMMARY TYPES
// ============================================

export interface EndOfDaySummary {
  total_sessions: number;
  completed_sessions: number;
  cancelled_sessions: number;
  active_sessions: number;
  total_boxes: number;
  voided_boxes: number;
  total_weight_lbs: number;
  total_waste_lbs: number;
  avg_yield_percentage: number;
}

// Operator efficiency score for a product
export interface OperatorEfficiency {
  employee_number: string;
  display_name: string;
  completed_sessions: number;
  avg_yield: number;
  avg_speed_lbs_hr: number;
  yield_rank: number;
  speed_rank: number;
  composite_rank: number;
}

export interface OperatorEfficiencyResponse {
  product_sku: string;
  operators: OperatorEfficiency[];
}

export interface ProductRawMaterial {
  item_number: string;
  item_name: string;
  item_chinese_name: string;
  allocated_weight: number;
  remainder_weight: number;
  used_weight: number | null;
  remainder_recorded: boolean;
}

// WCO-2607: one usable raw-material lot for the mid-session Add Material per-lot picker.
// Returned by GET /production/raw-material-lots (product-service rawMaterialLots.ts).
export interface RawMaterialLot {
  lot_id: string;
  item_number: string;
  transfer_number: string;
  lot_type: string | null;
  status: string | null;
  is_ni_remnant: boolean;
  current_amount: number;           // live remaining weight (lb)
  actual_quantity: number | null;   // RECEIVED cases; never decremented on consumption (WCO-2594)
  actual_weight: number | null;     // WCO-2594: received weight (lb) — basis for weight-derived remaining cases
  unit_weight: number | null;
  yield_factor: number | null;      // product-specific yield (decimal), null when unset
  item_english_name: string | null;
  item_chinese_name: string | null;
}

export interface ProductSummary {
  product_sku: string;
  product_name: string;
  product_chinese_name: string | null;
  sessions_count: number;
  boxes_packed: number;
  total_weight: number;
  yield_percentage: number;
  initial_material_weight?: number;
  raw_materials?: ProductRawMaterial[];
}

export interface StationSummary {
  station_id: number;
  station_name: string;
  location_name: string | null;
  sessions_count: number;
  boxes_packed: number;
  total_weight: number;
  active_hours: number;
}

export interface WorkerSummary {
  employee_number: string;
  worker_name: string;
  sessions_worked: number;
  boxes_packed: number;
  total_weight: number;
  avg_pace: number; // lbs per hour
}

export interface SessionSummary {
  session_number: string;
  product_sku: string;
  product_name: string;
  product_chinese_name: string | null;
  status: string;
  station_names: string[];
  operator_names: string[];
  created_at: string | null;
  completed_at: string | null;
  start_time: string | null;
  end_time: string | null;
  boxes_packed: number;
  expected_boxes: number;
  total_weight: number;
  target_weight: number;
  voided_boxes: number;
  waste_weight: number;
  remaining_weight: number;
  remaining_cases: number;
  yield_percentage: number;
  elapsed_seconds?: number;
}

export interface SummaryAlert {
  type: 'low_yield' | 'high_void_rate' | 'incomplete_sessions' | 'byproduct_aging';
  severity: 'warning' | 'info' | 'error';
  message: string;
  session_number?: string;
  session_numbers?: string[];
  // Structured params for localized message building (message is the English fallback)
  yield_percentage?: string;
  void_rate?: string;
  count?: number;
  // For byproduct_aging alerts
  lot_id?: number;
  transfer_number?: string;
  item_number?: string;
  age_days?: number;
  item_name?: string;
  current_amount?: string;
}

export interface WasteBreakdownItem {
  waste_type: string;
  total_weight: number;
  entry_count: number;
}

export interface WasteTypeEntry {
  waste_type: string;
  weight: number;
  entry_count: number;
  byproduct_item?: string | null;
  byproduct_item_name?: string | null;
}

export interface SessionWasteBreakdown {
  session_number: string;
  product_name: string;
  product_chinese_name: string;
  product_sku: string;
  waste_types: WasteTypeEntry[];
  total_waste: number;
}

export interface MaterialTrackingItem {
  item_number: string;
  item_name: string;
  allocated: number;
  remainder: number;
  used: number;
}

export interface MaterialTracking {
  total_initial_material: number;
  total_final_product: number;
  actual_waste: number;
  returns: number;
  by_products: number;
  returns_and_by_products: number;
  material_utilization: number;
  by_material: MaterialTrackingItem[];
}

export interface EndOfDaySummaryResponse {
  date: string;
  summary: EndOfDaySummary;
  by_product: ProductSummary[];
  by_station: StationSummary[];
  by_worker: WorkerSummary[];
  sessions: SessionSummary[];
  waste_breakdown: WasteBreakdownItem[];
  waste_by_session: SessionWasteBreakdown[];
  alerts: SummaryAlert[];
  material_tracking?: MaterialTracking | null;
}

export interface LocationBalance {
  locationCode: string;
  locationName: string;
  displayBalance: number;
  displayCasesPieces?: { cases: number; pieces: number };
}

export interface RawMaterial {
  item_number: string;
  transfer_number: string;
  item_english_name: string;
  item_chinese_name: string;
  yield_factor: number | null; // Decimal, read directly from ics.raw_material.yield_factor (product-specific row); null when unset
  yield_factor_source?: string | null; // 'raw_material' when set, else null (no fallback chain on /raw-materials)
  packing: number;
  unit_weight: number;
  category_code: string;
  reference_unit_cost: number;
  location_code: string | null;
  english_brand_name: string | null;
  chinese_brand_name: string | null;
  lot_location_code: string | null;
  current_stock: number;
  // WCO-2746: the REAL case count behind current_stock, summed from each lot's own cases:weight
  // ratio — NOT weight ÷ unit_weight (unit_weight is the item's catalog figure and does not
  // describe the cases on the floor). `current_stock - available_case_weight` is the loose tail
  // (RM-NI + part-case remainders), which is weight without a case.
  available_cases?: number;
  available_case_weight?: number;
  unit: string;
  is_available: boolean;
  reorder_point: number;
  preference?: number;
  is_remnant: boolean;
  base_item_number: string;
  is_ni_priority?: boolean; // True for NI (remainder_not_intact) materials - shown first
  on_hold_weight?: number; // Weight of this item currently on hold pending manager approval
  is_on_hold?: boolean; // True if this specific lot is from approved on-hold inventory
  has_lot_inventory?: boolean; // True if this material has lot inventory in HTE
  labor_factor?: number | null; // Labor factor from ics.raw_material
  labour_factor?: number | null; // Active Labour Factor reference for planning display
  labour_factor_reference_id?: number | null;
  labour_factor_reference_scope?: 'material' | 'global' | null;
  labour_factor_source?: 'trial' | 'organic' | 'manual' | null;
  labour_factor_baseline_throughput?: number | null;
  allowance_pct?: number | null; // WCO-2544: planning-only % derate of the standard lb/hr (0 = none)
  mean_output_lbs?: number | null; // Average actual output from completed sessions using this material
}

// ============================================
// LABOUR FACTOR (standard-based model, WCO-2474)
// ============================================
// One row per (finished product × raw-material input). The manager edits the standard
// throughput (lb per worker-hour); the labour_factor is derived server-side as
// baseline_throughput ÷ standard_throughput (2dp) and is null until the product has a
// designated baseline AND this row's throughput is set (renders as "—").
export interface LabourFactorMaterial {
  finished_product_item_number: string;
  raw_material_item_number: string;
  raw_material_name: string | null;
  raw_material_chinese_name?: string | null;
  preference: number | null;
  standard_throughput_lbs_per_worker_hour: number | null;
  is_lf_baseline: boolean;
  allowance_pct?: number | null; // WCO-2544: planning-only % derate of the standard lb/hr (0 = none)
  labour_factor: number | null; // derived (baseline ÷ material, 2dp); null when unset
  updated_by: string | null;
  updated_at: string | null;
}

// WCO-2524: one change-log entry for a (product, material). A throughput edit carries
// old/new_throughput; a baseline move carries old/new_is_baseline.
export interface LabourFactorHistoryEntry {
  id: number;
  change_type: 'throughput' | 'baseline';
  old_throughput: number | null;
  new_throughput: number | null;
  old_is_baseline: boolean | null;
  new_is_baseline: boolean | null;
  changed_by: string | null;
  changed_at: string;
}

export interface MaterialSummaryItem {
  item_number: string;
  available_weight: number;
  on_hold_weight: number;
  on_hold_approval_status: string | null;
}

export interface MaterialSummary {
  items: MaterialSummaryItem[];
  has_available: boolean;
  has_on_hold: boolean;
}

// Normalize yield fields from whole % (DB) to decimal (frontend).
const normalizeYieldField = (obj: any, field: string) => {
  if (obj && obj[field] != null) obj[field] = Number(obj[field]) / 100;
};

const productionService = {
  // Get all production sessions
  getProductionSessions: async (params?: { status?: string; unassigned?: boolean; include?: string }) => {
    const queryParams = new URLSearchParams();
    if (params?.status) queryParams.append('status', params.status);
    if (params?.unassigned) queryParams.append('unassigned', 'true');
    if (params?.include) queryParams.append('include', params.include);

    const queryString = queryParams.toString();
    const response = await api.get(`/production/sessions${queryString ? `?${queryString}` : ''}`);
    const sessions = response.data;
    for (const session of sessions) {
      if (session.sub_sessions) {
        for (const sub of session.sub_sessions) {
          normalizeYieldField(sub, 'yield_percentage');
        }
      }
    }
    return sessions;
  },

  // Get daily work queue (flat list of sub-sessions with parent session data)
  getDailyWorkQueue: async (date?: string): Promise<DailyWorkQueueItem[]> => {
    const queryParams = new URLSearchParams();
    if (date) queryParams.append('date', date);
    queryParams.append('include', 'sub_sessions');

    const queryString = queryParams.toString();
    const response = await api.get(`/production/sessions${queryString ? `?${queryString}` : ''}`);
    const sessions = response.data;

    const items: DailyWorkQueueItem[] = [];
    for (const session of sessions) {
      if (session.sub_sessions && session.sub_sessions.length > 0) {
        for (const sub of session.sub_sessions) {
          items.push({
            sub_session_number: sub.sub_session_number,
            session_number: session.session_number,
            product_name: session.product_name,
            product_chinese_name: session.product_chinese_name || null,
            product_sku: session.product_sku,
            station_id: sub.station_id || null,
            station_name: sub.station_name || null,
            operator_name: sub.operator_name || null,
            operator_employee_number: sub.operator_employee_number || null,
            packer_name: sub.packer_name || null,
            packer_employee_number: sub.packer_employee_number || null,
            status: sub.status,
            expected_boxes: Number(sub.expected_boxes) || 0,
            boxes_produced: Number(sub.boxes_produced) || 0,
            scheduled_date: session.scheduled_date || session.created_at,
            actual_output_weight: Number(sub.actual_output_weight) || 0,
            target_output_weight: Number(sub.target_output_weight) || 0,
            initial_material_weight: Number(sub.initial_material_weight) || 0,
            yield_percentage: sub.yield_percentage != null ? Number(sub.yield_percentage) : undefined,
            started_at: sub.started_at || null,
            paused_duration_seconds: Number(sub.paused_duration_seconds) || 0,
            queue_position: sub.queue_position != null ? Number(sub.queue_position) : null,
            operator_status: sub.operator_status || 'scanned_out',
            packer_status: sub.packer_status || 'scanned_out',
          });
        }
      }
    }
    return items;
  },

  // Get single production session
  getProductionSession: async (sessionId: string) => {
    const response = await api.get(`/production/sessions/${sessionId}`);
    const data = response.data;
    if (data.sub_sessions) {
      for (const sub of data.sub_sessions) {
        normalizeYieldField(sub, 'yield_percentage');
      }
    }
    return data;
  },

  // Get session history and packed boxes
  getSessionHistory: async (sessionId: string): Promise<{
    history: Array<{
      id: number;
      action: string;
      old_status: string | null;
      new_status: string | null;
      changed_by: string | null;
      notes: string | null;
      created_at: string;
      worker_name: string | null;
      employee_number: string | null;
      role: string | null;
      station_name: string | null;
      boxes_at_event: number | null;
      weight_at_event: number | null;
      boxes_packed_during_shift: number | null;
      weight_packed_during_shift: number | null;
    }>;
    boxes: Array<{
      id: number;
      box_number: string;
      barcode_number: string | null;
      item_number: string | null;
      product_name: string | null;
      actual_weight: number;
      status: string;
      packed_at: string;
      packed_by: string | null;
      packed_by_name: string | null;
      voided_at: string | null;
      voided_by: string | null;
      void_reason: string | null;
      lot_number: string | null;
      material_type: string | null;
      sub_session_number: string | null;
      station_name: string | null;
      total_prints: number | null;
      discarded_labels: Array<{
        print_number: number;
        status: string;
        label_data: any;
        printed_at: string;
        printed_by: string;
        printed_by_name: string;
        discarded_at: string;
        discarded_by: string;
        discarded_by_name: string;
        discard_reason: string;
      }> | null;
    }>;
    packerSummary: Array<{
      employee_number: string;
      role: string;
      worker_name: string;
      total_boxes: number;
      total_weight: number;
      total_elapsed_seconds: number;
    }>;
  }> => {
    const response = await api.get(`/production/sessions/${sessionId}/history`);
    return response.data;
  },

  // Add session history entries (batch insert)
  addSessionHistory: async (sessionId: string, entries: Array<{ action: string; changed_by?: string; notes?: Record<string, unknown> | string }>) => {
    const response = await api.post(`/production/sessions/${sessionId}/history`, { entries });
    return response.data;
  },

  // Create new production session
  createProductionSession: async (data: CreateProductionSessionRequest) => {
    const response = await api.post('/production/sessions', data);
    return response.data;
  },

  // Update production session
  updateProductionSession: async (sessionId: string, data: Partial<CreateProductionSessionRequest>) => {
    const response = await api.put(`/production/sessions/${sessionId}`, data);
    return response.data;
  },

  // Set source location for a session (creates inbound transfer)
  setSourceLocation: async (sessionId: string, sourceWarehouse: string) => {
    const response = await api.post(`/production/sessions/${sessionId}/set-source-location`, { source_warehouse: sourceWarehouse });
    return response.data;
  },

  // Cancel production session
  // WCO-2706: authorization is the api-gateway supervisor role gate on this route. The endpoint
  // re-checks the session status server-side and no longer accepts the retired `force` flag.
  cancelSession: async (sessionId: string, reason: string) => {
    const response = await api.post(`/production/sessions/${sessionId}/cancel`, { reason });
    return response.data;
  },

  // Get cancelled sessions with submitted transfers not yet acknowledged
  getCancelledTransferReminders: async () => {
    const response = await api.get('/production/sessions/cancelled-transfer-reminders');
    return response.data.reminders;
  },

  // Dismiss a transfer reminder for a cancelled session (acknowledged but still shows in notifications)
  dismissTransferReminder: async (sessionNumber: string) => {
    const response = await api.post(`/production/sessions/${sessionNumber}/dismiss-transfer-reminder`, { dismissed_by: 'manager' });
    return response.data;
  },

  // Resolve a transfer reminder (fully removed from notifications — VB6 transfer confirmed deleted)
  resolveTransferReminder: async (sessionNumber: string) => {
    const response = await api.post(`/production/sessions/${sessionNumber}/resolve-transfer-reminder`, { resolved_by: 'manager' });
    return response.data;
  },

  // Get sessions with excessive label reprints (3+), not yet resolved
  getReprintAlerts: async () => {
    const response = await api.get('/production/sessions/reprint-alerts');
    return response.data.alerts;
  },

  // Resolve a reprint alert
  resolveReprintAlert: async (sessionNumber: string) => {
    const response = await api.post(`/production/sessions/${sessionNumber}/resolve-reprint-alert`, { resolved_by: 'manager' });
    return response.data;
  },

  // Get finish_product_not_intact lots eligible for the given session
  // (status='finish_product_ni', current_amount > 0, item_number = session.product_sku)
  getFinishProductLots: async (sessionNumber: string): Promise<Array<{
    lot_id: string;
    item_number: string;
    transfer_number: string;
    current_amount: number;
    received_date: string | null;
    received_by: string | null;
    item_english_name: string | null;
    item_chinese_name: string | null;
  }>> => {
    const response = await api.get('/production/finish-product-lots', { params: { sessionNumber } });
    return response.data?.finish_product_lots || [];
  },

  // WCO-2607: get the individual usable lots of a raw material for a product, for the
  // mid-session Add Material per-lot picker. Same usable-lot filter as the aggregate
  // getRawMaterials, but one row per lot (with lot_id + is_ni_remnant + cases + yield).
  getRawMaterialLots: async (productSku: string, itemNumber: string): Promise<RawMaterialLot[]> => {
    const response = await api.get('/production/raw-material-lots', { params: { productSku, itemNumber } });
    return response.data?.raw_material_lots || [];
  },

  // Get raw materials linked to a specific product via production_inputs
  getRawMaterials: async (productSku?: string): Promise<{ raw_materials: RawMaterial[]; material_summary: MaterialSummary }> => {
    const response = await api.get('/production/raw-materials', {
      params: productSku ? { productSku } : {}
    });
    const raw_materials = response.data?.raw_materials || response.data || [];
    const material_summary: MaterialSummary = response.data?.material_summary || { items: [], has_available: false, has_on_hold: false };
    return { raw_materials, material_summary };
  },

  // Get HTE finished products that have production_inputs entries
  // Uses inventory-service with filters instead of product-service
  getProducts: async () => {
    const response = await api.get('/inventory/products', {
      params: {
        brand: 'HTE,H T E',
        hasProductionInputs: 'true',
        type: 'finish_product',
        noPagination: 'true',
        sortBy: 'name-asc'
      }
    });
    const products = response.data?.data?.products || [];
    // Map inventory-service field names to the legacy field names used by components
    // WeeklyPlanningView uses item_number/item_english_name, other components use sku/name
    return products.map((p: any) => ({
      ...p,
      item_number: p.sku,
      item_english_name: p.name,
      item_chinese_name: p.chinese_name,
      target_weight: p.packing_display || p.packing,
      packing_display: p.packing_display || p.packing,
      unit_price: p.reference_unit_cost,
      brand: p.english_brand_name
    }));
  },

  // Search all HTE finished products (including those without raw material linkages)
  searchAllProducts: async (search: string) => {
    const response = await api.get('/inventory/products', {
      params: {
        brand: 'HTE,H T E',
        type: 'finish_product',
        search,
        noPagination: 'true',
        sortBy: 'name-asc'
      }
    });
    const products = response.data?.data?.products || [];
    return products.map((p: any) => ({
      ...p,
      item_number: p.sku,
      item_english_name: p.name,
      item_chinese_name: p.chinese_name,
      target_weight: p.packing_display || p.packing,
      packing_display: p.packing_display || p.packing,
      unit_price: p.reference_unit_cost,
      brand: p.english_brand_name
    }));
  },

  // Queue session to station
  queueSession: async (sessionId: string, stationId: number) => {
    const response = await api.post(`/production/sessions/${sessionId}/queue`, { station_id: stationId });
    return response.data;
  },

  // Get station queue
  getStationQueue: async (stationId: string) => {
    const response = await api.get(`/production/sessions/queue/${stationId}`);
    return response.data;
  },

  // Remove session from queue
  removeFromQueue: async (sessionId: string) => {
    const response = await api.delete(`/production/sessions/${sessionId}/queue`);
    return response.data;
  },

  // Get sub-sessions for a session
  getSubSessions: async (sessionId: string): Promise<SubSession[]> => {
    const response = await api.get(`/production/sessions/${sessionId}/sub-sessions`);
    const data = response.data;
    for (const sub of data) {
      normalizeYieldField(sub, 'yield_percentage');
    }
    return data;
  },

  // Assign stations to an existing session (deferred multi-station assignment)
  assignStations: async (sessionId: string, stationAllocations: StationAllocation[]) => {
    const response = await api.post(`/production/sessions/${sessionId}/assign-stations`, {
      station_allocations: stationAllocations
    });
    return response.data;
  },

  // Add sub-sessions to existing session (append mode - doesn't delete existing sub-sessions)
  addSubSessions: async (sessionId: string, allocations: StationAllocation[]) => {
    const response = await api.post(`/production/sessions/${sessionId}/add-sub-sessions`, {
      allocations
    });
    return response.data;
  },

  // ============================================
  // END OF DAY SUMMARY
  // ============================================

  // Get end-of-day summary for a specific date
  getEndOfDaySummary: async (date?: string): Promise<EndOfDaySummaryResponse> => {
    const params = date ? `?date=${date}` : '';
    const response = await api.get(`/supervisor/end-of-day-summary${params}`);
    const data = response.data;
    normalizeYieldField(data.summary, 'avg_yield_percentage');
    if (data.by_product) {
      for (const p of data.by_product) normalizeYieldField(p, 'yield_percentage');
    }
    if (data.sessions) {
      for (const s of data.sessions) normalizeYieldField(s, 'yield_percentage');
    }
    return data;
  },

  // Dismiss an alert for today (used for byproduct_aging alerts)
  dismissAlert: async (alertKey: string, dismissedBy?: string): Promise<{ status: string; message: string }> => {
    const response = await api.post('/supervisor/alerts/dismiss', {
      alert_key: alertKey,
      dismissed_by: dismissedBy
    });
    return response.data;
  },

  // ============================================
  // MOBILE SUPERVISOR FUNCTIONS
  // ============================================

  // Get worker stats for a specific date
  getWorkerStats: async (date: string): Promise<{ workers: any[] }> => {
    try {
      const response = await api.get(`/supervisor/worker-stats`, { params: { date } });
      return response.data;
    } catch (error) {
      return { workers: [] };
    }
  },

  // Get override history
  getOverrideHistory: async (date: string): Promise<{ records: any[] }> => {
    try {
      const response = await api.get(`/supervisor/override-history`, {
        params: { date_from: date, date_to: date }
      });
      // Map backend format to frontend format
      const overrides = response.data.overrides || [];
      return {
        records: overrides.map((o: any) => ({
          id: o.id,
          override_type: o.override_type,
          supervisor_name: o.supervisor_name,
          supervisor_employee_number: o.details?.supervisor_employee_number || '',
          operator_name: o.operator_name,
          operator_employee_number: o.operator_employee_number,
          session_number: o.session_number || o.sub_session_number,
          product_sku: o.product_number,
          product_name: o.product_name,
          box_number: o.details?.box_number,
          details: JSON.stringify(o.details || {}),
          reason: o.reason,
          created_at: o.created_at
        }))
      };
    } catch (error) {
      return { records: [] };
    }
  },

  // Create a session (for mobile production planning)
  createSession: async (data: any) => {
    const response = await api.post('/production/sessions', data);
    return response.data;
  },

  // Get the yield factor for a single raw material (decimal; null when unset).
  // Pass productSku to resolve the product-specific row (mirrors /byproduct-mappings);
  // without it, resolves by name only (no product context, e.g. barcode lookup).
  getYieldFactor: async (itemNumber: string, productSku?: string): Promise<{ yield_factor: number | null; source: string | null }> => {
    const response = await api.get('/production/raw-materials/yield-factor', {
      params: productSku ? { itemNumber, productSku } : { itemNumber }
    });
    return response.data;
  },

  // Get yield factors for all raw materials of a product (decimal; null when unset)
  getYieldFactorBatch: async (productSku: string): Promise<{
    product_sku: string;
    materials: Array<{ item_number: string; yield_factor: number | null; source: string | null }>;
  }> => {
    const response = await api.get('/production/raw-materials/yield-factor/batch', {
      params: { productSku }
    });
    return response.data;
  },

  // ============================================
  // LABOUR FACTOR (standard-based model, WCO-2474)
  // ============================================
  // Reaches product-service's /labour-factor router via the api-gateway prefix
  // /api/production/labour-factor (gateway strips /api/production → /labour-factor).

  // List a finished product's raw-material inputs with editable throughput + derived LF.
  getLabourFactorMaterials: async (productItem: string): Promise<{
    success: boolean;
    productItem: string;
    materials: LabourFactorMaterial[];
  }> => {
    const response = await api.get(
      `/production/labour-factor/products/${encodeURIComponent(productItem)}/materials`
    );
    return response.data;
  },

  // Set (or clear, with null) one input's standard throughput in lb per worker-hour.
  updateLabourFactorThroughput: async (
    productItem: string,
    rawItem: string,
    standardThroughput: number | null,
    updatedBy?: string
  ): Promise<{ success: boolean; standard: any }> => {
    const response = await api.put(
      `/production/labour-factor/products/${encodeURIComponent(productItem)}/materials/${encodeURIComponent(rawItem)}`,
      { standardThroughput, updatedBy }
    );
    return response.data;
  },

  // WCO-2544: set the planning-only allowance % (0–100) for one input. Derates only the
  // AddSessionModal output estimate — does NOT change the displayed Labour Factor (1.x).
  updateLabourFactorAllowance: async (
    productItem: string,
    rawItem: string,
    allowancePct: number,
    updatedBy?: string
  ): Promise<{ success: boolean; standard: any }> => {
    const response = await api.put(
      `/production/labour-factor/products/${encodeURIComponent(productItem)}/materials/${encodeURIComponent(rawItem)}/allowance`,
      { allowancePct, updatedBy }
    );
    return response.data;
  },

  // Designate which input is the product's baseline (LF = 1.0). Server recomputes all rows.
  setLabourFactorBaseline: async (
    productItem: string,
    rawItem: string,
    updatedBy?: string
  ): Promise<{ success: boolean; baseline: any }> => {
    const response = await api.put(
      `/production/labour-factor/products/${encodeURIComponent(productItem)}/baseline`,
      { rawItem, updatedBy }
    );
    return response.data;
  },

  // WCO-2524: change log for one (product, material), newest first (for the history popover).
  getLabourFactorHistory: async (
    productItem: string,
    rawItem: string
  ): Promise<{ success: boolean; history: LabourFactorHistoryEntry[] }> => {
    const response = await api.get(
      `/production/labour-factor/products/${encodeURIComponent(productItem)}/materials/${encodeURIComponent(rawItem)}/history`
    );
    return response.data;
  },

  // ============================================
  // ITEM SUMMARY REPORT
  // ============================================

  getItemSummaryRawMaterials: async (params: {
    search?: string; brand?: string; location?: string;
    startDate?: string; endDate?: string; page?: number; limit?: number;
  }) => {
    const response = await api.get('/production/raw-materials/item-summary', { params });
    return response.data;
  },

  getItemSummaryFinished: async (params: {
    search?: string; brand?: string; location?: string;
    startDate?: string; endDate?: string; page?: number; limit?: number;
  }) => {
    const response = await api.get('/production/raw-materials/item-summary/finished', { params });
    return response.data;
  },

  getItemSummaryBrands: async () => {
    const response = await api.get('/production/raw-materials/item-summary/brands');
    return response.data;
  },

  // ============================================
  // CONFIG SETTINGS
  // ============================================

  // Fetch all config settings from ics.hte_config_settings
  getConfigSettings: async (): Promise<Array<{ setting_key: string; setting_value: string; description: string; updated_by: string | null; updated_at: string }>> => {
    const response = await api.get('/settings');
    return response.data;
  },

  // WCO-3061: valid HTE-family warehouse codes for transfer-back source setting
  getHteWarehouses: async (): Promise<Array<{ warehouse_code: string; description: string }>> => {
    const response = await api.get('/settings/hte-warehouses');
    return response.data;
  },

  // Update a single config setting by key
  updateConfigSetting: async (key: string, value: string): Promise<{ setting_key: string; setting_value: string }> => {
    const response = await api.put(`/settings/${key}`, { value });
    return response.data;
  },

  // Create a new config setting
  createConfigSetting: async (key: string, value: string, description?: string): Promise<{ setting_key: string; setting_value: string }> => {
    const response = await api.post('/settings', { key, value, description });
    return response.data;
  },

  // Delete a config setting
  deleteConfigSetting: async (key: string): Promise<void> => {
    await api.delete(`/settings/${key}`);
  },

  // Packing material thresholds (WCO-2155)
  // safe_pieces / reorder_pieces / pieces_per_bundle / current_stock_bundles are resolved
  // SERVER-side (WCO-2155) — the client must not re-derive pieces-per-bundle from the
  // packing string, nor divide pieces into bundles itself. Null = unresolvable.
  getPackingThresholds: async (): Promise<Array<{ id: number; item_number: string; safe_quantity: number; reorder_quantity: number; item_name: string; packing: string | null; item_unit: string | null; item_sub_unit: string | null; item_sub_quantity: number | null; current_stock: number; safe_pieces: number | null; reorder_pieces: number | null; pieces_per_bundle: number | null; current_stock_bundles: number | null; current_stock_remainder: number | null }>> => {
    const response = await api.get('/settings/packing-thresholds');
    return response.data;
  },

  updatePackingThreshold: async (itemNumber: string, safe_quantity: number, reorder_quantity: number): Promise<any> => {
    const response = await api.put(`/settings/packing-thresholds/${encodeURIComponent(itemNumber)}`, { safe_quantity, reorder_quantity });
    return response.data;
  },

  deletePackingThreshold: async (itemNumber: string): Promise<void> => {
    await api.delete(`/settings/packing-thresholds/${encodeURIComponent(itemNumber)}`);
  },

  getPackingMaterials: async (): Promise<Array<{ item_number: string; item_name: string; packing: string | null; current_stock: number; safe_quantity: number | null; reorder_quantity: number | null }>> => {
    const response = await api.get('/settings/packing-materials');
    return response.data;
  },

  getPackingReplenishmentAlerts: async (): Promise<Array<{ transfer_number: string; status: string; source_warehouse: string; created_at: string; items: Array<{ item_number: string; item_name: string | null; quantity: number }> }>> => {
    const response = await api.get('/settings/packing-replenishment-alerts');
    return response.data;
  },

  cancelPackingReplenishmentTransfer: async (transferNumber: string): Promise<void> => {
    await api.delete(`/settings/packing-replenishment-alerts/${encodeURIComponent(transferNumber)}`);
  },

  // Fetch yield variance threshold (decimal)
  getYieldThresholds: async (): Promise<{ yieldThreshold: number }> => {
    const response = await api.get('/settings/yield-thresholds');
    return response.data;
  },

  // Fetch operator efficiency scores for a product
  getOperatorEfficiency: async (productSku: string, sortBy: 'yield' | 'speed' | 'both' = 'both'): Promise<OperatorEfficiencyResponse> => {
    const response = await api.get('/production/operators/efficiency', {
      params: { productSku, sortBy }
    });
    return response.data;
  },

  // Download session report as PDF
  downloadSessionPdf: async (sessionNumber: string): Promise<Blob> => {
    const response = await api.get(`/production/sessions/${encodeURIComponent(sessionNumber)}/pdf`, {
      responseType: 'blob'
    });
    return response.data;
  },
  // WCO-2776: per-location balances from the QS Inventory service. The browser holds no
  // QS URL, key or logic — product-service performs the reads and returns the same
  // LocationBalance shape the old endpoint did, so the display components are unchanged.
  // Fails soft: on any error the modal renders no balances rather than breaking.
  getQsInventoryBatch: async (
    itemNumbers: string[],
    location?: string,
    excludeSessionNumber?: string,
  ): Promise<{ results: Record<string, LocationBalance[]>; safety: Record<string, number> }> => {
    const unique = [...new Set(itemNumbers)];
    if (unique.length === 0) return { results: {}, safety: {} };
    try {
      const response = await api.post('/production/qs-inventory/batch', { itemNumbers: unique, location, excludeSessionNumber });
      return {
        results: (response.data?.results ?? {}) as Record<string, LocationBalance[]>,
        safety: (response.data?.safety ?? {}) as Record<string, number>,
      };
    } catch {
      return { results: {}, safety: {} };
    }
  },

  // WCO-2776: safe-quantity verdicts for the session create/edit pre-check. Replaces the
  // per-item transfer-check loop the modal used to run itself; the comparison now happens
  // in the backend. Verdict shape is unchanged, so the caller's `decision` handling stands.
  checkQsSafeQuantities: async (
    items: Array<{ itemNumber: string; quantity: number }>,
    location: string,
    excludeSessionNumber?: string,
  ): Promise<Array<{ itemNumber: string; requestedQuantity: number; availableBalance: number; safetyQuantity: number; decision: string; cappedQuantity: number | null }>> => {
    if (items.length === 0) return [];
    const response = await api.post('/production/qs-inventory/safe-check', { items, location, excludeSessionNumber });
    return response.data?.results ?? [];
  },
};

export default productionService;
